import { useApolloClient } from '@apollo/client';
import {
  type ActionHandler,
  CheckboxField,
  Create,
  type Nullable,
  type ObjectSchemaDefinition,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import { Field } from 'formik';
import * as lscache from 'lscache';
import { SignInMethod } from 'models/signInMethod';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import {
  type CreateTenantAdministratorMutation,
  type CreateTenantAdministratorMutationVariables,
  useCreateTenantAdministratorMutation,
  useResetTenantAdministratorPasswordMutation,
} from '../../../../generated/id-service-env-administration';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';

export const tenantAdministratorCreateSchema =
  Yup.object<ObjectSchemaDefinition>({
    name: Yup.string().required('Name is a required field').max(255),

    email: Yup.string().required().email(),
  });

type FormData = CreateTenantAdministratorMutationVariables['input'] & {
  generateCredentials: boolean;
};

type SubmitResponse = Nullable<
  CreateTenantAdministratorMutation['createTenantAdmin']
>;

export const TenantAdministratorCreate: React.FC = () => {
  const signInMethod = lscache.get('signInMethod');

  const apolloClient = useApolloClient();

  const [tenantAdminCreate] = useCreateTenantAdministratorMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const [resetTenantAdminPassword] =
    useResetTenantAdministratorPasswordMutation({
      client: apolloClient,
      fetchPolicy: 'no-cache',
    });

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      const createdTenantAdmin = (
        await tenantAdminCreate({
          variables: {
            input: {
              name: formData.name,
              email: formData.email,
            },
          },
        })
      ).data?.createTenantAdmin;

      if (
        formData.generateCredentials === true &&
        signInMethod === SignInMethod.DIRECT
      ) {
        await resetTenantAdminPassword({
          variables: { input: { id: createdTenantAdmin?.tenantAdmin?.id } },
        });
      }

      return createdTenantAdmin ?? null;
    },
    [tenantAdminCreate, signInMethod, resetTenantAdminPassword],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      if (submitResponse?.tenantAdmin) {
        history.push(`/tenantAdmin/${submitResponse.tenantAdmin.id}`);
      } else {
        // The schema has the response.data properties marked as optional, since theoretically a user could have
        // permissions to mutate but not to read. In practice this can not happen on that service, so we just throw
        // an error in case we get there.
        throw new Error('Not expected');
      }
    },
    [history],
  );

  const [message] = useState<StationMessage | undefined>(() => {
    if (signInMethod === SignInMethod.DIRECT) {
      return {
        title:
          'The generated credentials will be sent via an email depending on the selection below. Credentials can also be generated later in the Tenant Administrator details station.',
        type: 'info',
      };
    }
  });

  return (
    <Create<FormData, SubmitResponse>
      title="New Administrator"
      validationSchema={tenantAdministratorCreateSchema}
      saveData={saveData}
      onProceed={onProceed}
      cancelNavigationUrl={'/tenantAdmin'}
      initialData={{
        data: {
          name: '',
          email: '',
          generateCredentials: true,
        },
        loading: false,
      }}
      stationMessage={message}
    >
      <Form signInMethod={signInMethod} />
    </Create>
  );
};

const Form: React.FC<{ signInMethod: SignInMethod }> = ({ signInMethod }) => {
  return (
    <>
      <Field name="name" label="Name" as={SingleLineTextField} />
      <Field name="email" label="Email" as={SingleLineTextField} />
      {signInMethod === SignInMethod.DIRECT && (
        <Field
          name="generateCredentials"
          label="Generate Credentials"
          as={CheckboxField}
        />
      )}
    </>
  );
};

export const TenantAdministratorCreateCrumb: BreadcrumbResolver = () =>
  'New Administrator';
